// Impresión EN LOTE de etiquetas propias Lynx (Admin → Envíos → Impresión
// de etiquetas). Mismo principio que packageLabel.ts (traer el SVG por
// fetch e imprimir desde una ventana propia, nunca abrir el endpoint
// directo porque el token no viaja solo en la URL) pero para N envíos a
// la vez y con picker de formato -- el armado de HTML/CSS de impresión
// vive en labelPrint.ts, compartido con packageLabel.ts.
//
// El backend (POST /v2/deliveries/labels/print) reutiliza labelService
// (ensureCode/labelFor) para cada id -- esta función no genera ningún
// SVG nuevo, solo arma la vista imprimible con lo que el server devuelve.

import { authFetch } from "./authFetch";
import type { PackageLabel } from "./packageLabel";
import { LabelPrintFormat, LABEL_PRINT_FORMATS, buildPrintableHtml, labelSvgPages, openPrintWindow } from "./labelPrint";

export type { LabelPrintFormat };
export { LABEL_PRINT_FORMATS };

export type PrintLabelsBatchResult = {
  labels: PackageLabel[];
  skipped: number;
};

export async function fetchLabelsBatch(
  ids: number[],
  { token }: { token: string | null }
): Promise<PrintLabelsBatchResult> {
  const response = await authFetch("/v2/deliveries/labels/print", {
    token,
    method: "POST",
    json: true,
    body: JSON.stringify({ ids }),
  });
  const json = await response.json().catch(() => ({} as any));
  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || `Error ${response.status}`);
  }
  return { labels: json.labels ?? [], skipped: Number(json.skipped) || 0 };
}

// Separado de fetchLabelsBatch a propósito (bug corregido, ver "LYNX --
// cambio mínimo External API/etiquetas"): antes el backend marcaba
// label_printed_at dentro del mismo POST que traía las etiquetas, es
// decir ANTES de saber si la ventana de impresión se iba a poder abrir.
// Si el navegador bloqueaba el popup, el envío quedaba marcado como
// impreso sin haberse impreso nunca. Ahora se llama recién después de que
// openPrintWindow() confirma (sin lanzar) que la ventana se abrió.
async function markLabelsPrinted(ids: number[], { token }: { token: string | null }): Promise<void> {
  const response = await authFetch("/v2/deliveries/labels/mark-printed", {
    token,
    method: "POST",
    json: true,
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    const json = await response.json().catch(() => ({} as any));
    throw new Error(json?.error || `Error ${response.status}`);
  }
}

// Marca label_printed_at en el server (recién después de confirmar que la
// ventana de impresión se abrió) y abre la ventana de impresión. Reimprimir
// está permitido a propósito: esta misma función sirve para ambos casos,
// sin distinción.
export async function printDeliveryLabelsBatch(
  ids: number[],
  format: LabelPrintFormat,
  { token }: { token: string | null }
): Promise<PrintLabelsBatchResult> {
  const result = await fetchLabelsBatch(ids, { token });

  if (!result.labels.length) {
    throw new Error("Ninguno de los envíos seleccionados tiene etiqueta Lynx válida");
  }

  const pages = labelSvgPages(result.labels, format);
  const html = buildPrintableHtml(format, pages, `Etiquetas (${result.labels.length})`);
  // Lanza si el popup fue bloqueado -- en ese caso nunca se llega a marcar
  // como impreso, que es exactamente el fix.
  openPrintWindow(html);

  try {
    await markLabelsPrinted(ids, { token });
  } catch {
    // La impresión ya se disparó; no marcar label_printed_at no debe
    // bloquear ni revertir eso, solo queda sin reflejarse en el listado.
  }

  return result;
}
